import { createContext, useCallback, useContext, useState } from 'react';
import type { ClipboardEvent, FormEvent, ReactNode } from 'react';

import { ApiProblem } from '../api/problem';
import type { Limit } from '../generated/limits';
import { violationText } from '../i18n';
import type { Translate } from '../i18n';
import { useSession } from '../session/useSession';

export type { Limit } from '../generated/limits';
export { LIMITS } from '../generated/limits';

/**
 * Les erreurs que le contrôle d'avant envoi (useFormCheck) a posées, par
 * identifiant de champ. Field les affiche quand le serveur n'a rien dit.
 */
const FormCheckContext = createContext<Readonly<Record<string, string>>>({});

/**
 * Un champ : libellé, contrôle, aide, et l'erreur que le serveur a rattachée à
 * ce champ (ou, à défaut, celle du contrôle d'avant envoi). L'erreur est reliée
 * au contrôle par `aria-describedby`, que le lecteur d'écran annonce au moment
 * où le champ reçoit le focus. `required` met l'astérisque après le libellé ; le
 * contrôle, lui, porte `aria-required`.
 */
export function Field({
    id,
    label,
    hint,
    error,
    required = false,
    children,
}: {
    readonly id: string;
    readonly label: string;
    readonly hint?: string | undefined;
    readonly error?: string | null | undefined;
    readonly required?: boolean;
    readonly children: ReactNode;
}): ReactNode {
    const checked = useContext(FormCheckContext)[id];
    const shown = error ?? checked;

    return (
        <div className="field">
            <label className="field__label" htmlFor={id}>
                {label}
                {required ? <RequiredMark /> : null}
            </label>
            {children}
            {hint === undefined ? null : (
                <p className="field__hint" id={`${id}-hint`}>
                    {hint}
                </p>
            )}
            {shown === undefined || shown === null ? null : (
                <p className="field__error" id={`${id}-error`}>
                    {shown}
                </p>
            )}
        </div>
    );
}

/** L'astérisque d'un champ obligatoire : vu, pas lu (le contrôle porte aria-required). */
export function RequiredMark(): ReactNode {
    return (
        <span className="field__required" aria-hidden="true">
            *
        </span>
    );
}

/**
 * Un champ texte complet (D70) : libellé, astérisque, longueur maximale, compteur,
 * texte collé coupé et signalé, erreurs. La limite vient de l'entité
 * (`limit={LIMITS.Person.lastName}`, front/src/generated/limits.ts) ; `required`
 * ne sert que quand l'écran s'écarte de la règle de l'entité.
 *
 * Le compteur est toujours là sous une zone de texte, et sous une ligne à partir
 * de 90 % de la limite ; il passe en ambre à 90 %.
 */
export function TextField({
    id,
    label,
    value,
    onChange,
    limit,
    max,
    required,
    hint,
    error,
    multiline = false,
    rows = 3,
    type = 'text',
    placeholder,
    autoComplete,
    inputMode,
    dir,
    className,
    disabled = false,
    readOnly = false,
    autoFocus = false,
    mono = false,
    onBlur,
}: {
    readonly id: string;
    readonly label: string;
    readonly value: string;
    onChange(value: string): void;
    readonly limit?: Limit | undefined;
    readonly max?: number | undefined;
    readonly required?: boolean | undefined;
    readonly hint?: string | undefined;
    readonly error?: string | null | undefined;
    readonly multiline?: boolean;
    readonly rows?: number;
    readonly type?: 'text' | 'email' | 'tel' | 'url' | 'search';
    readonly placeholder?: string | undefined;
    readonly autoComplete?: string | undefined;
    readonly inputMode?: 'text' | 'numeric' | 'decimal' | 'tel' | 'email' | 'url' | 'search' | undefined;
    readonly dir?: 'ltr' | 'rtl' | 'auto' | undefined;
    readonly className?: string | undefined;
    readonly disabled?: boolean;
    readonly readOnly?: boolean;
    readonly autoFocus?: boolean;
    readonly mono?: boolean;
    onBlur?(): void;
}): ReactNode {
    const { t } = useSession();
    const checked = useContext(FormCheckContext)[id];
    const [cut, setCut] = useState(false);
    const limitMax = max ?? limit?.max ?? undefined;
    const isRequired = required ?? limit?.required ?? false;
    const shownError = error ?? checked ?? null;
    const showCounter = limitMax !== undefined && limitMax !== null && (multiline || value.length >= 0.9 * limitMax);
    const near = limitMax !== undefined && limitMax !== null && value.length >= 0.9 * limitMax;
    const cutHint = cut && limitMax !== undefined && limitMax !== null ? t('form.pasteCut', { max: limitMax }) : undefined;
    const help = cutHint ?? hint;
    const aria = describedBy(id, help !== undefined, shownError);
    const classes = ['field__input', multiline ? 'field__input--area' : '', mono ? 'field__input--mono' : '', className ?? ''].filter(Boolean).join(' ');

    const paste = (event: ClipboardEvent<HTMLInputElement | HTMLTextAreaElement>): void => {
        if (limitMax === undefined || limitMax === null) return;

        const target = event.currentTarget;
        const selected = (target.selectionEnd ?? 0) - (target.selectionStart ?? 0);
        const pasted = event.clipboardData.getData('text').length;
        setCut(target.value.length - selected + pasted > limitMax);
    };

    const common = {
        id,
        className: classes,
        value,
        placeholder,
        autoComplete,
        dir,
        disabled,
        readOnly,
        autoFocus,
        onBlur,
        ...(limitMax === undefined || limitMax === null ? {} : { maxLength: limitMax }),
        ...(isRequired ? { 'aria-required': true as const } : {}),
        ...aria,
        onPaste: paste,
    };

    return (
        <div className="field">
            <label className="field__label" htmlFor={id}>
                {label}
                {isRequired ? <RequiredMark /> : null}
            </label>
            {multiline ? (
                <textarea
                    {...common}
                    rows={rows}
                    onChange={(event) => {
                        if (cut && event.target.value.length < (limitMax ?? Infinity)) setCut(false);
                        onChange(event.target.value);
                    }}
                />
            ) : (
                <input
                    {...common}
                    type={type}
                    inputMode={inputMode}
                    onChange={(event) => {
                        if (cut && event.target.value.length < (limitMax ?? Infinity)) setCut(false);
                        onChange(event.target.value);
                    }}
                />
            )}
            {help === undefined && !showCounter ? null : (
                <div className="field__foot">
                    {help === undefined ? null : (
                        <p className="field__hint" id={`${id}-hint`}>
                            {help}
                        </p>
                    )}
                    {showCounter ? (
                        <span className={`field__count${near ? ' field__count--near' : ''}`} aria-hidden="true">
                            {value.length} / {limitMax}
                        </span>
                    ) : null}
                </div>
            )}
            {shownError === null ? null : (
                <p className="field__error" id={`${id}-error`}>
                    {shownError}
                </p>
            )}
        </div>
    );
}

/**
 * Le contrôle d'avant envoi (D70) : un champ `aria-required` vide arrête l'envoi.
 * Chaque champ dit « Ce champ est obligatoire. », un encadré les liste (chacun
 * est un lien), le premier reçoit le curseur. Le serveur reste juge du reste.
 */
export function useFormCheck(): {
    readonly errors: Readonly<Record<string, string>>;
    readonly missing: readonly { readonly id: string; readonly label: string }[];
    check(form: HTMLFormElement | null): boolean;
    onInput(event: FormEvent<HTMLFormElement>): void;
    reset(): void;
} {
    const { t } = useSession();
    const [missing, setMissing] = useState<readonly { readonly id: string; readonly label: string }[]>([]);

    const check = useCallback(
        (form: HTMLFormElement | null): boolean => {
            if (form === null) return true;

            const empty = [...form.querySelectorAll<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>('input[aria-required="true"], textarea[aria-required="true"], select[aria-required="true"]')].filter((element) => !element.disabled && element.value.trim() === '');
            const found = empty.map((element) => ({
                id: element.id,
                label: (form.querySelector(`label[for="${CSS.escape(element.id)}"]`)?.textContent ?? element.getAttribute('aria-label') ?? element.id).replace(/\*\s*$/, '').trim(),
            }));
            setMissing(found);
            empty[0]?.focus();

            return found.length === 0;
        },
        [],
    );

    const errors: Record<string, string> = {};

    for (const item of missing) {
        errors[item.id] = t('form.required');
    }

    return {
        errors,
        missing,
        check,
        onInput(event) {
            const id = (event.target as HTMLElement).id;

            if (id !== '' && missing.some((item) => item.id === id)) {
                setMissing(missing.filter((item) => item.id !== id));
            }
        },
        reset() {
            setMissing([]);
        },
    };
}

/** Donne aux champs d'un formulaire les erreurs du contrôle d'avant envoi. */
export function FormCheckProvider({ errors, children }: { readonly errors: Readonly<Record<string, string>>; readonly children: ReactNode }): ReactNode {
    return <FormCheckContext.Provider value={errors}>{children}</FormCheckContext.Provider>;
}

/** L'encadré des champs à corriger, en tête du formulaire. */
export function ErrorSummary({ missing }: { readonly missing: readonly { readonly id: string; readonly label: string }[] }): ReactNode {
    const { t } = useSession();

    if (missing.length === 0) return null;

    return (
        <div className="alert alert--error form-summary" role="alert">
            <strong>{t('form.summary', { count: missing.length })}</strong>
            <span className="form-summary__links">
                {missing.map((item, index) => (
                    <span key={item.id}>
                        {index > 0 ? ' · ' : ''}
                        <button type="button" className="form-summary__link" onClick={() => document.getElementById(item.id)?.focus()}>
                            {item.label}
                        </button>
                    </span>
                ))}
            </span>
        </div>
    );
}

/** « Les champs marqués * sont obligatoires » : visible seulement quand le formulaire en a. */
export function FormLegend(): ReactNode {
    const { t } = useSession();

    const [before, after = ''] = t('form.legend').split('*');

    return (
        <span className="form-legend">
            {before}
            <span className="field__required">*</span>
            {after}
        </span>
    );
}

/**
 * Un formulaire hors dialogue, avec le contrôle d'avant envoi : `onSubmit` n'est
 * appelé que si les champs obligatoires sont remplis.
 */
export function CheckedForm({ className, onSubmit, children }: { readonly className?: string | undefined; onSubmit(): void; readonly children: ReactNode }): ReactNode {
    const form = useFormCheck();

    return (
        <form
            className={['checked-form', className ?? ''].filter(Boolean).join(' ')}
            noValidate
            onInput={form.onInput}
            onSubmit={(event) => {
                event.preventDefault();

                if (form.check(event.currentTarget)) onSubmit();
            }}
        >
            <ErrorSummary missing={form.missing} />
            <FormCheckProvider errors={form.errors}>{children}</FormCheckProvider>
        </form>
    );
}

/** Les attributs ARIA d'un contrôle, selon qu'il a une aide et une erreur. */
export function describedBy(id: string, hint: boolean, error: string | null | undefined): { 'aria-describedby'?: string; 'aria-invalid'?: true } {
    const ids = [hint ? `${id}-hint` : null, error ? `${id}-error` : null].filter((value): value is string => value !== null);

    return {
        ...(ids.length > 0 ? { 'aria-describedby': ids.join(' ') } : {}),
        ...(error ? { 'aria-invalid': true as const } : {}),
    };
}

export function Alert({ tone, children }: { readonly tone: 'success' | 'error' | 'info'; readonly children: ReactNode }): ReactNode {
    return (
        <div className={`alert alert--${tone}`} role={tone === 'error' ? 'alert' : 'status'}>
            {children}
        </div>
    );
}

/**
 * Le message à afficher pour une erreur qui n'est rattachée à aucun champ.
 *
 * Le `detail` d'un 403 est écrit pour un humain par le serveur (AccountPolicy) et
 * dit POURQUOI — « ce compte atteint au-delà de votre portée » : il vaut mieux
 * que le générique. Les erreurs 5xx, elles, ne montrent jamais leur détail.
 */
export function problemMessage(t: Translate, cause: unknown): string {
    if (!(cause instanceof ApiProblem)) {
        return t('common.error');
    }

    if (cause.status === 403) {
        return cause.detail !== '' ? cause.detail : t('common.forbidden');
    }

    if (cause.status === 404) {
        return t('common.notFound');
    }

    if (cause.status >= 400 && cause.status < 500 && cause.detail !== '') {
        return cause.detail;
    }

    return t('common.error');
}

/** Les erreurs de champ d'un 422, par chemin, traduites à partir de leur code. */
export function fieldErrors(t: Translate, cause: unknown): Readonly<Record<string, string>> {
    if (!(cause instanceof ApiProblem) || cause.status !== 422) {
        return {};
    }

    const errors: Record<string, string> = {};

    for (const violation of cause.violations) {
        errors[violation.propertyPath] ??= violationText(t, violation);
    }

    return errors;
}
